import React, { useEffect, useState, useCallback } from 'react';
import styled from 'App/styled';
import { useTheme } from 'emotion-theming';
import Portal from 'shared/components/Portal';
import YesTick from 'components/Icons/YesTick';
import WarningIcon from 'components/Icons/warning';

/**
 * Visually hidden but in the a11y tree for aria-live (not display:none).
 */
const SrOnlyLiveRegion = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const FALLBACK_ID = 'snackbar';
const FALLBACK_ACTIVE_CLASS = 'active';
const DISPLAY_TIME = 2000;
const WAIT_TIME_FOR_CLOSE_ANIMATION = 300; // This is equal to CSS transition animation duration;
const CART_COLOR =
  'linear-gradient(0deg, rgba(101, 119, 134, 0.16), rgba(101, 119, 134, 0.16)), linear-gradient(0deg, rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03)), #0E0F10';
const CUSTOM_WIDTH = '344px';

interface SnackbarTheme {
  background: string;
  customWidth: string;
}

const Wrapper = styled.div`
  position: fixed;
  z-index: 12;
  bottom: 10px;
  left: 0;
  right: 0;
  background: transparent;
  justify-content: center;
  display: flex;
  .container {
    position: relative;
    display: flex;
    border-radius: 5px;
    max-width: 384px;
    margin-bottom: 15px;
    align-items: center;
    background: ${({ background }: SnackbarTheme) => background};
    color: #fff;
    font-weight: 600;
    min-height: 49px;
    padding: 16px;
    width: ${({ customWidth }: SnackbarTheme) => customWidth};
    // margin-bottom: -80px;
    transition: all 0.3s;

    &.${FALLBACK_ACTIVE_CLASS} {
      margin-bottom: 0;
    }

    .body {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: space-between;
      ${({ theme }) => theme.typography.subTitleLarge};
    }

    .icon {
      margin-right: 8px;
      svg {
        vertical-align: middle;
      }
    }

    .close-snackbar {
      background: transparent;
      padding: 4px 10px;
      ${({ theme }) => theme.typography.titleSmall};
      border: none;
      color: #fff;
      cursor: pointer;
      &:focus {
        outline: none;
      }
    }
  }
`;

const TextButton = styled.div`
  margin: 0 10px;
  color: ${({ theme }) => theme.brandLight};
  cursor: pointer;
`;

const Icon = styled.span`
  margin-right: 8px;
  display: flex;
  align-items: center;
  path {
    fill: white;
  }
`;

const MessageBody = styled.div`
  flex: 1;
  min-width: 0;
`;

interface SnackbarProps {
  content: string | React.ReactNode;
  autoClose: boolean;
  closeCallback: Function;
  errorType?: boolean;
  snackBarId: string;
  text?: string;
  handleTextClick?: () => void;
}

function Snackbar({
  content,
  autoClose,
  closeCallback,
  errorType = false,
  snackBarId = FALLBACK_ID,
  text = '',
  handleTextClick = () => {},
}: SnackbarProps) {
  const [isMounted, setIsMounted] = useState(false);
  // Live-region delay only applies when `content` is a string.
  const messageText = typeof content === 'string' ? content.trim() : '';
  const [srAnnouncement, setSrAnnouncement] = useState('');
  const theme: Theme = useTheme();
  const handleMount = useCallback(() => {
    if (isMounted) {
      const snackBarNode = document.getElementById(snackBarId);
      if (snackBarNode) {
        snackBarNode.classList.add(FALLBACK_ACTIVE_CLASS);
      }
    }
  }, [isMounted, snackBarId]);

  const handleClose = useCallback(() => {
    if (isMounted) {
      const snackBarNode = document.getElementById(snackBarId);
      if (snackBarNode) {
        snackBarNode.classList.remove(FALLBACK_ACTIVE_CLASS);
        setTimeout(() => {
          closeCallback();
        }, WAIT_TIME_FOR_CLOSE_ANIMATION);
      }
    }
  }, [isMounted, snackBarId, closeCallback]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Start live region empty, then set text so SRs pick up the change.
  useEffect(() => {
    if (!messageText) {
      setSrAnnouncement('');
      return;
    }
    setSrAnnouncement('');
    const timeoutId = window.setTimeout(() => {
      setSrAnnouncement(messageText);
    }, 100);
    return () => window.clearTimeout(timeoutId);
  }, [messageText, snackBarId]);

  useEffect(() => {
    handleMount();
  }, [handleMount]);

  useEffect(() => {
    if (autoClose) {
      setTimeout(() => {
        handleClose();
      }, DISPLAY_TIME);
    }
  }, [autoClose, handleClose]);

  const showText = text && !errorType;
  let background = errorType ? theme.colors.orange600 : theme.brandPrimary;
  background = showText ? CART_COLOR : background;
  const customWidth = showText ? CUSTOM_WIDTH : 'auto';
  const showErrorIcon = errorType && !showText;
  const showTickIcon = !errorType && !showText;
  return (
    <Portal>
      <Wrapper
        background={background}
        customWidth={customWidth}
      >
        <div
          className="container"
          id={snackBarId}
        >
          <div className="body">
            {showTickIcon && (
              <i
                className="icon"
                aria-hidden="true"
              >
                <YesTick />
              </i>
            )}
            {showErrorIcon && (
              <Icon aria-hidden="true">
                <WarningIcon />
              </Icon>
            )}
            {messageText ? (
              <SrOnlyLiveRegion
                id={`${snackBarId}-a11y-announce`}
                role={errorType ? 'alert' : 'status'}
                aria-live={errorType ? 'assertive' : 'polite'}
                aria-atomic="true"
              >
                {srAnnouncement}
              </SrOnlyLiveRegion>
            ) : null}
            <MessageBody
              {...(messageText && { 'aria-hidden': true })}
              {...(!messageText && {
                role: errorType ? 'alert' : 'status',
                'aria-live': errorType ? 'assertive' : 'polite',
                'aria-atomic': true,
              })}
            >
              {content}
            </MessageBody>
            {showText && (
              <TextButton
                id="view_bag_snackbar"
                onClick={handleTextClick}
              >
                View Bag
              </TextButton>
            )}
          </div>
          {!autoClose && (
            <button
              type="button"
              className="close-snackbar"
              onClick={handleClose}
            >
              &#10006;
            </button>
          )}
        </div>
      </Wrapper>
    </Portal>
  );
}

export default Snackbar;
